import { defineStore, storeToRefs } from "pinia";
import { useCurationExperienceStore } from "@/features/experience-curation/stores/useCurationExperienceStore";
import { useCurrentExperienceCategoriesInterestsQuery } from "@/features/experience-curation/queries/useExperienceCategoriesInterestsQuery";
import { useHasFunctionalNestedFieldChange } from "@/features/experience-curation/queries/useHasRawFieldChange";
import { useMasterData } from "@/stores/master-data";
import { areNonCommercialFieldsEditableForUser } from "@/features/experience-curation/lib/viewTypeUtils";
import { useComplexModelModelWithChanges } from "@/features/core-shared/composables/useComplexModelWithChanges";

export const useCurationCategories = defineStore("curation-setting-categories-field", () => {
  const curationExperienceStore = useCurationExperienceStore();
  const masterData = useMasterData();
  const { experienceCategories, experienceInterests } = storeToRefs(masterData);
  const categoriesInterestsQuery = useCurrentExperienceCategoriesInterestsQuery();

  const initialValue = computed(() => ({
    categories: categoriesInterestsQuery.data.value?.categories ?? [],
    interests: categoriesInterestsQuery.data.value?.interests ?? [],
  }));

  const { model, hasChanges, isFilled, reset } = useComplexModelModelWithChanges(initialValue);

  const categories = computed({
    get: () => model.value.categories,
    set: (value: string[]) => {
      model.value = { ...model.value, categories: value };
    },
  });

  const interests = computed({
    get: () => model.value.interests,
    set: (value: string[]) => {
      model.value = { ...model.value, interests: value };
    },
  });

  const categoriesRelationId = computed(() => categoriesInterestsQuery.data.value?.categoriesId);
  const interestsRelationId = computed(() => categoriesInterestsQuery.data.value?.interestsId);

  const isRequired = true;
  const isReadonly = computed(() => curationExperienceStore.isReadonly || !areNonCommercialFieldsEditableForUser());
  const isVisible = computed(() => !curationExperienceStore.isCommercialView);

  const hasUnpublishedChange = useHasFunctionalNestedFieldChange((f) => ({
    categories: f?.categories,
    interests: f?.interests,
  }));

  return {
    id: "categories",
    categories,
    interests,
    categoriesRelationId,
    interestsRelationId,
    experienceCategories,
    experienceInterests,
    isRequired,
    isReadonly,
    isVisible,
    hasChanges,
    hasUnpublishedChange,
    isFilled,
    reset,
  };
});
